import { Chart, DoughnutController, BarController, CategoryScale, LinearScale, BarElement, ArcElement } from 'chart.js'
import { findAncestor, hex2rgba, isElementVisible } from './utils';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import ChartDeferred from 'chartjs-plugin-deferred';

import {CategoryScale} from 'chart.js'; 


Chart.register(DoughnutController)
Chart.register(BarController)
Chart.register(CategoryScale);
Chart.register(LinearScale);
Chart.register(BarElement);
Chart.register(ArcElement);
Chart.register(ChartDataLabels);
Chart.register(ChartDeferred);

/**
 * Initialize stacked bars chart
 * @param canvas
 */
export function initializeStackedBarsChart(canvas) {
    // Do not initialize if chart isn't visible or if it is initialized already
    if (false === isChartVisible(canvas) || true === isChartInitialized(canvas)) {
        return
    }

    const classes = {
        activeStat: 'campaign-chart-stat--active'
    }

    const wrapper = findAncestor(canvas, '.campaign-chart-wrapper')
    const stats = Array.from(wrapper.querySelectorAll('.campaign-chart-stat'))
    const ctx = canvas.getContext('2d')
    const chartData = getChartData(canvas.id)

    if(chartData.labels.length) {
        canvas.style.maxWidth = chartData.labels.length * 135 + "px";
    }

    const config = {
        type: 'bar',
        data: chartData,
        options: {
            responsive: true,
            maintainAspectRatio: false,
            borderWidth: 0,
            borderRadius: 5,
            borderSkipped: 'bottom',
            onClick: (e, elems, chart) => onChartInteraction(e, elems, chart),
            onHover: (e, elems, chart) => onChartInteraction(e, elems, chart),
            datasets: {
                bar: {
                    maxBarThickness: 96,
                }
            },
            transitions: {
                resize: {
                    animation: {
                        duration: 0
                    }
                },
                active: {
                    animations: {
                      x: {
                        from: 0
                      },
                      y: {
                        from: 0
                      }
                    }
                },
            },
            layout: {
                padding: {
                    top: 30,
                    left: 0,
                    right: 0
                }
            },
            plugins: {
                deferred: {
                    xOffset: 150,
                    yOffset: '50%', 
                    delay: 500
                },
                datalabels: {
                    font: {
                        size: 14,
                        family: "Noto Ikea"
                    },
                    anchor: 'end',
                    align: 'top',
                    formatter: (value, ctx) => {
                        const datasetArray = [];
                        ctx.chart.data.datasets.forEach(dataset => {
                            if (dataset.data[ctx.dataIndex] !== undefined) {
                                datasetArray.push(parseFloat(dataset.data[ctx.dataIndex]))
                            }
                        });
                        function totalSum(total, datapoint) {
                            return total + datapoint;
                        }

                        let sum = datasetArray.reduce(totalSum, 0);
                        if (ctx.datasetIndex === datasetArray.length - 1 && sum !== 0) {
                            return chartData.prefix + sum.toLocaleString('en') + chartData.suffix;
                        } else {
                            return '';
                        }
                    }
                },
                legend: {
                    display: false
                },
                tooltip: {
                    enabled: true,
                    backgroundColor: '#111111',
                    padding: 12,
                    caretSize: 0,
                    boxPadding: 5,
                    boxWidth: 14,
                    boxHeight: 14,
                    position: 'average',
                    titleMarginBottom: 10,
                    footerMarginTop: 10,
                    titleFont: {
                        size: 12,
                        family: "Noto Ikea",
                        weight: 400
                    },
                    bodyFont: {
                        size: 12,
                        family: "Noto Ikea",
                    },
                    footerFont: {
                        size: 16,
                        family: "Noto Ikea",
                        weight: 700
                    },
                    multiKeyBackground: '#111111',
                    callbacks: {
                        title: function() {
                            return;
                        },
                        label: function(tooltipItem) {
                            return tooltipItem.dataset.label;
                        },
                        footer: function(tooltipItem) {
                            if(tooltipItem[0].dataset.displayValues[tooltipItem[0].dataIndex]) {
                                return tooltipItem[0].dataset.displayValues[tooltipItem[0].dataIndex];
                            } else {
                                return chartData.prefix + tooltipItem[0].formattedValue + chartData.suffix;
                            }
                        },
                    }
                }
            },
            scales: {
                x: {
                    stacked: true,
                    grid: {
                        display: false,
                        drawBorder: false,
                    },
                    ticks: {
                        font: {
                            size: 12,
                            family: "Noto Ikea"
                        }
                    }
                },
                y: {
                    stacked: true,
                    display: false
                }
            }
        }
    }

    // Create chart instance
    new Chart(ctx, config)

    // Set data attribute
    canvas.dataset.initialized = true

    // Make all stats inactive when the mouse leaves the canvas
    canvas.addEventListener('mouseleave', function () {
        stats.forEach(stat => setActiveStat(stat, false))
    })

    /**
     * On interaction with chart arcs
     * @param e
     * @param activeElems
     * @param chart
     */
    function onChartInteraction(e, activeElems, chart) {
        if(stats.length === 0) {
            return false;
        }
        // Make all stats inactive
        stats.forEach(stat => setActiveStat(stat, false))
        // If we interact with something, make it active
        if (activeElems.length > 0) {
            activeElems.forEach(el => setActiveStat(getStat(el.datasetIndex), true))
        }
    }

    /**
     * Set active stat
     * @param el
     * @param active
     */
    function setActiveStat(el, active) {
        if(!el) {
            return;
        }

        if (active === true) {
            el.classList.add(classes.activeStat)
        } else {
            el.classList.remove(classes.activeStat)
        }
    }

    /**
     * Get stat by chart index
     * @param chartIndex
     * @returns {HTMLElement|null}
     */
    function getStat(chartIndex) {
        let targetStat = null
        for (let stat of stats) {
            if (chartIndex === parseInt(stat.dataset.index)) {
                targetStat = stat
                break
            }
        }

        return targetStat
    }

    /**
     * Get chart data
     * @param chartVariable
     * @returns {*|null}
     */
    function getChartData(chartVariable) {
        let data = window.hasOwnProperty(chartVariable) ? JSON.parse(window[chartVariable]) : null
        if (data === null) {
            return null
        }

        data.datasets.map(dataset => {
            if(dataset.backgroundColor) {
                dataset.hoverBackgroundColor = hex2rgba(dataset.backgroundColor, 0.85)
            }
        });

        return data;
    }
}

/**
 * Initialize doughnut chart
 * @param canvas
 */
export function initializeDoughnutChart(canvas) {
    // Do not initialize if chart isn't visible or if it is initialized already
    if (false === isChartVisible(canvas) || true === isChartInitialized(canvas)) {
        return
    }

    const classes = {
        activeStat: 'campaign-chart-stat--active'
    }

    const wrapper = findAncestor(canvas, '.campaign-chart-wrapper')
    const stats = Array.from(wrapper.querySelectorAll('.campaign-chart-stat'))
    const ctx = canvas.getContext('2d')
    const chartData = getChartData(canvas.id)
    const config = {
        type: 'doughnut',
        data: chartData,
        options: {
            responsive: true,
            borderWidth: 0,
            onClick: (e, elems, chart) => onChartInteraction(e, elems, chart),
            onHover: (e, elems, chart) => onChartInteraction(e, elems, chart),
            animation: {
                animateRotate: true,
                animateScale: true
            },
            transitions: {
                resize: {
                    animation: {
                        duration: 0
                    }
                },
            },
            plugins: {
                deferred: {
                    xOffset: 150,
                    yOffset: '50%',
                    delay: 100
                },
                datalabels: false,
                legend: {
                    display: false,
                },
                tooltip: {
                    enabled: false
                }
            }
        }
    }

    // Create chart instance
    new Chart(ctx, config)

    // Set data attribute
    canvas.dataset.initialized = true

    // Make all stats inactive when the mouse leaves the canvas
    canvas.addEventListener('mouseleave', function () {
        stats.forEach(stat => setActiveStat(stat, false))
    })

    /**
     * On interaction with chart arcs
     * @param e
     * @param activeElems
     * @param chart
     */
    function onChartInteraction(e, activeElems, chart) {
        // Make all stats inactive
        stats.forEach(stat => setActiveStat(stat, false))

        // If we interact with something, make it active
        if (activeElems.length > 0) {
            activeElems.forEach(el => setActiveStat(getStat(el.index), true))
        }
    }

    /**
     * Get active stats
     * @returns {T[]}
     */
    function getActiveStats() {
        return stats.filter(el => el.classList.contains(classes.activeStat))
    }

    /**
     * Set active stat
     * @param el
     * @param active
     */
    function setActiveStat(el, active) {
        if(!el) {
            return;
        }

        if (active === true) {
            el.classList.add(classes.activeStat)
        } else {
            el.classList.remove(classes.activeStat)
        }
    }

    /**
     * Get stat by chart index
     * @param chartIndex
     * @returns {HTMLElement|null}
     */
    function getStat(chartIndex) {
        let targetStat = null
        for (let stat of stats) {
            if (chartIndex === parseInt(stat.dataset.index)) {
                targetStat = stat
                break
            }
        }

        return targetStat
    }

    /**
     * Get chart data
     * @param chartVariable
     * @returns {*|null}
     */
    function getChartData(chartVariable) {
        let data = window.hasOwnProperty(chartVariable) ? JSON.parse(window[chartVariable]) : null
        if (data === null) {
            return null
        }

        const colors = data.colors.map(hex => hex2rgba(hex))

        return {
            labels: data.labels,
            datasets: [{
                data: data.data,
                backgroundColor: colors,
                hoverBackgroundColor: data.colors.map(hex => hex2rgba(hex, 0.85)),
            }],
        }
    }
}

/**
 * Check if chart is visible or not
 * @param canvas
 * @returns {boolean}
 */
export function isChartVisible(canvas) {
    return isElementVisible(canvas)
}

/**
 * Check if chart is initialized or not
 * @param canvas
 * @returns {boolean}
 */
export function isChartInitialized(canvas) {
    // data attribute values will always be strings
    return 'true' === canvas.dataset.initialized
}

/**
 * Listen for DOMContentLoaded event
 */
document.addEventListener('DOMContentLoaded', function () {
    const doughnutCharts = Array.from(document.querySelectorAll('.doughnut-chart-canvas:not(.accordion *)'))
    doughnutCharts.forEach(chart => initializeDoughnutChart(chart))

    const stackedBarsCharts = Array.from(document.querySelectorAll('.stacked-bars-chart-canvas:not(.accordion *)'))
    stackedBarsCharts.forEach(chart => initializeStackedBarsChart(chart))

    // Init in accordions when accordion is opened
    let accordions = document.querySelectorAll('.accordion');
    if (accordions.length) {

        accordions.forEach(el => {

            el.querySelector('.accordion__toggle').addEventListener('click', e => {
                const doughnutCharts = Array.from(el.querySelectorAll('.doughnut-chart-canvas'))
                doughnutCharts.forEach(chart => initializeDoughnutChart(chart))
            
                const stackedBarsCharts = Array.from(el.querySelectorAll('.stacked-bars-chart-canvas'))
                stackedBarsCharts.forEach(chart => initializeStackedBarsChart(chart))
            });

        });
    }
})
